import type { UIMessage } from "ai";
import type { inferRouterOutputs } from "@trpc/server";
import { createIntl, defineMessages, type IntlShape } from "react-intl";
import type { AppRouter } from "@runcheetah/api/router";
import {
  hasActiveBrandAssistantStreamMetadata,
  parseBrandAssistantStreamMetadata,
} from "@runcheetah/shared";

import { buildBrandPath } from "@/hooks/use-route-brand";

const brandAssistantChatHistoryMessages = defineMessages({
  messageCount: {
    id: "pulse.chatHistory.messageCount",
    defaultMessage: "{count, plural, one {# message} other {# messages}}",
  },
});

const defaultBrandAssistantChatHistoryIntl = createIntl({ locale: "en", messages: {} });

type RouterOutputs = inferRouterOutputs<AppRouter>;
export type BrandAssistantChatSummary =
  RouterOutputs["brandAssistantChat"]["list"]["chats"][number];

export const PENDING_BRAND_ASSISTANT_CHAT_ID = "00000000-0000-4000-8000-000000000000";
export const NEW_BRAND_ASSISTANT_CHAT_SLUG = "new";
const BRAND_ASSISTANT_CHAT_TITLE_MAX_LENGTH = 80;

export const buildBrandAssistantChatPath = (brandId: string, chatId: string) =>
  buildBrandPath(brandId, `/pulse/${chatId}`);

export const buildBrandAssistantNewChatPath = (brandId: string) =>
  buildBrandPath(brandId, `/pulse/${NEW_BRAND_ASSISTANT_CHAT_SLUG}`);

export const isBrandAssistantPendingChatRoute = (chatId: string | undefined) =>
  chatId === NEW_BRAND_ASSISTANT_CHAT_SLUG || chatId === PENDING_BRAND_ASSISTANT_CHAT_ID;

export type BrandAssistantCreatedChatTransition = {
  chatId: string;
  runtimeKey: string;
};

export const getBrandAssistantChatRuntimeKey = ({
  chatId,
  routeChatIsPending,
  pendingLocationKey,
  createdChatTransition,
}: {
  chatId: string | undefined;
  routeChatIsPending: boolean;
  pendingLocationKey: string;
  createdChatTransition: BrandAssistantCreatedChatTransition | null;
}) => {
  if (routeChatIsPending) {
    return `pending-${pendingLocationKey}`;
  }

  if (chatId && createdChatTransition?.chatId === chatId) {
    return createdChatTransition.runtimeKey;
  }

  return chatId ?? "pending";
};

export const getLatestBrandAssistantUserMessage = (
  messages: readonly UIMessage[],
): UIMessage | null => messages.findLast((message) => message.role === "user") ?? null;

export const getRetryableBrandAssistantUserMessage = (
  messages: readonly UIMessage[],
): UIMessage | null => {
  const latestUserIndex = messages.findLastIndex((message) => message.role === "user");
  if (latestUserIndex === -1) {
    return null;
  }

  const latestUser = messages[latestUserIndex];
  if (!latestUser) {
    return null;
  }

  const assistantMessages = messages
    .slice(latestUserIndex + 1)
    .filter((message) => message.role === "assistant");
  if (assistantMessages.length === 0) {
    return latestUser;
  }

  return assistantMessages.some(
    (message) => parseBrandAssistantStreamMetadata(message)?.status === "failed",
  )
    ? latestUser
    : null;
};

export const isBrandAssistantResponsePersisted = ({
  messages,
  userMessageId,
}: {
  messages: readonly UIMessage[];
  userMessageId: string;
}) => {
  const userMessageIndex = messages.findIndex(
    (message) => message.id === userMessageId && message.role === "user",
  );
  if (userMessageIndex === -1) {
    return false;
  }

  return messages.slice(userMessageIndex + 1).some((message) => {
    if (message.role !== "assistant") {
      return false;
    }

    const streamMetadata = parseBrandAssistantStreamMetadata(message);
    return streamMetadata?.status !== "streaming" && streamMetadata?.status !== "failed";
  });
};

export const deriveBrandAssistantChatTitleFromPrompt = (prompt: string): string => {
  const normalized = prompt.replace(/\s+/g, " ").trim();
  if (!normalized) {
    return "New chat";
  }

  if (normalized.length <= BRAND_ASSISTANT_CHAT_TITLE_MAX_LENGTH) {
    return normalized;
  }

  return `${normalized.slice(0, BRAND_ASSISTANT_CHAT_TITLE_MAX_LENGTH - 1).trimEnd()}...`;
};

export const getBrandAssistantChatMessageCountLabel = (
  messageCount: number,
  intl?: Pick<IntlShape, "formatMessage">,
) =>
  (intl ?? defaultBrandAssistantChatHistoryIntl).formatMessage(
    brandAssistantChatHistoryMessages.messageCount,
    { count: messageCount },
  );

export const getBrandAssistantChatTimestamp = (chat: BrandAssistantChatSummary) =>
  chat.lastMessageAt ?? chat.updatedAt;

export const isBrandAssistantChatPersistedAfterFirstTurn = ({
  messages,
  minimumMessageCount,
}: {
  messages: readonly UIMessage[];
  minimumMessageCount: number;
}) =>
  messages.length >= minimumMessageCount &&
  !messages.some((message) => hasActiveBrandAssistantStreamMetadata(message));

export function resolveBrandAssistantChatSnapshotMessages({
  currentMessages,
  nextMessages,
}: {
  currentMessages: readonly UIMessage[];
  nextMessages: readonly UIMessage[];
}): UIMessage[] {
  const currentLatestUserIndex = currentMessages.findLastIndex(
    (message) => message.role === "user",
  );
  if (currentLatestUserIndex === -1) {
    return [...nextMessages];
  }

  const currentLatestUser = currentMessages[currentLatestUserIndex];
  if (!currentLatestUser) {
    return [...nextMessages];
  }

  const nextLatestUserIndex = nextMessages.findIndex(
    (message) => message.id === currentLatestUser.id && message.role === "user",
  );
  if (nextLatestUserIndex === -1) {
    return [...currentMessages];
  }

  const currentAssistant = currentMessages
    .slice(currentLatestUserIndex + 1)
    .find((message) => message.role === "assistant");
  if (!currentAssistant) {
    return [...nextMessages];
  }

  const nextAssistantIndex = nextMessages.findIndex(
    (message, index) =>
      index > nextLatestUserIndex &&
      message.id === currentAssistant.id &&
      message.role === "assistant",
  );
  if (nextAssistantIndex === -1) {
    return getAssistantMessageText(currentAssistant).trim()
      ? [...currentMessages]
      : [...nextMessages];
  }

  const nextAssistant = nextMessages[nextAssistantIndex];
  if (!nextAssistant) {
    return [...nextMessages];
  }

  const currentAssistantText = getAssistantMessageText(currentAssistant);
  const nextAssistantText = getAssistantMessageText(nextAssistant);
  if (currentAssistantText.length <= nextAssistantText.length) {
    return [...nextMessages];
  }

  return nextMessages.map((message, index) => {
    if (index !== nextAssistantIndex) {
      return message;
    }

    return {
      ...message,
      parts: [
        ...currentAssistant.parts.filter(isAssistantTextLikePart),
        ...message.parts.filter((part) => !isAssistantTextLikePart(part)),
      ] as UIMessage["parts"],
    };
  });
}

function getAssistantMessageText(message: Pick<UIMessage, "parts">) {
  return message.parts
    .map((part) => (isAssistantTextLikePart(part) ? part.text : ""))
    .filter(Boolean)
    .join("\n\n");
}

function isAssistantTextLikePart(part: UIMessage["parts"][number]) {
  return part.type === "text" || part.type === "reasoning";
}

export const shouldResetBrandAssistantChatState = ({
  chatId,
  loadedChatId,
  lastResetChatId,
  isBusy = false,
}: {
  chatId: string | undefined;
  loadedChatId: string | null | undefined;
  lastResetChatId: string | null;
  isBusy?: boolean;
}) => Boolean(!isBusy && chatId && loadedChatId === chatId && loadedChatId !== lastResetChatId);

export const shouldRedirectUnavailableBrandAssistantChat = ({
  chatId,
  chat,
  isError,
}: {
  chatId: string | undefined;
  chat: unknown | null | undefined;
  isError: boolean;
}) => Boolean(chatId && (isError || chat === null));
